import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Link2, ArrowUpRight, Sparkles, Network, FileText } from "lucide-react";
import { api } from "../lib/api";

const TYPE_META = {
  dossier: {
    Icon: FileText,
    accent: "text-[#ffd60a]",
    accentBorder: "hover:border-[#ffd60a]/60",
    label: "ARCHIVED",
  },
  ai_dossier: {
    Icon: Sparkles,
    accent: "text-[#00ff88]",
    accentBorder: "hover:border-[#00ff88]/60",
    label: "AI COMPILED",
  },
  connection: {
    Icon: Network,
    accent: "text-pink-400",
    accentBorder: "hover:border-pink-400/60",
    label: "CONNECTION",
  },
};

/**
 * Universal cross-reference list — works for dossiers, AI deep dives, and
 * connections. Pulls related files of ALL types so a reader can keep going.
 */
export default function CrossReferences({ targetType, targetId, limit = 6 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!targetType || !targetId) return;
    let alive = true;
    setLoading(true);
    api
      .get(`/cross-references/${targetType}/${targetId}`, {
        params: { limit },
        timeout: 10000,
      })
      .then((r) => {
        if (alive) setItems(r.data || []);
      })
      .catch(() => {
        if (alive) setItems([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [targetType, targetId, limit]);

  if (loading) {
    return (
      <section className="mt-16 no-print">
        <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500">
          LOADING CROSS-REFERENCES...
        </div>
      </section>
    );
  }
  if (!items.length) return null;

  return (
    <section data-testid="cross-references" className="mt-16 no-print">
      <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 mb-3 flex items-center gap-2">
        <Link2 size={11} /> CROSS-REFERENCED FILES // {items.length}
      </div>
      <h3 className="font-heading font-black uppercase tracking-tight text-2xl text-white mb-6 border-b border-white/10 pb-3">
        If you read this, read these
      </h3>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((it) => {
          const meta = TYPE_META[it.type] || TYPE_META.dossier;
          const Icon = meta.Icon;
          return (
            <Link
              key={`${it.type}-${it.id}`}
              to={it.route}
              data-testid={`xref-${it.type}-${it.id}`}
              className={`group block border border-white/10 bg-[#0F0F0F] ${meta.accentBorder} hover:bg-[#141414] transition-colors p-5`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className={`font-mono text-[9px] tracking-[0.25em] ${meta.accent} flex items-center gap-2`}>
                  <Icon size={11} /> {meta.label}
                </div>
                <ArrowUpRight
                  size={14}
                  className={`text-zinc-500 group-hover:${meta.accent} transition-colors shrink-0`}
                />
              </div>
              <h4 className="font-heading font-black uppercase tracking-tight text-lg text-white leading-tight mb-2 group-hover:text-white transition-colors">
                {it.title}
              </h4>
              <p className="text-xs text-zinc-400 leading-relaxed line-clamp-2">
                {(it.summary || "").replace(/[#*_`]/g, "")}
              </p>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
